import React, { useState, useEffect } from 'react';
import { Button, Card, NavigationBar, ThemedScreen } from '../../shared/components';
import { chatService } from '../../shared/services';
import { Chat, Message } from '../../shared/types';

const ChatListScreen: React.FC<{ userName: string; onNavigate: (screen: string) => void }> = ({ userName, onNavigate }) => {
  const [chats, setChats] = useState<Chat[]>([]);
  const [tutorMode, setTutorMode] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);

  useEffect(() => {
    setChats(chatService.getChats());
    setTutorMode(localStorage.getItem('calmi_profile_mode') === 'tutor');
  }, []);

  const topics = [
    { id: 'day', label: 'Mi día', emoji: '🌟', message: '¡Hola! Cuéntame, ¿cómo estuvo tu día hoy? 🌟' },
    { id: 'school', label: 'Escuela', emoji: '📚', message: '¡Hola! ¿Cómo te fue en la escuela hoy? ¿Aprendiste algo nuevo? 📚' },
    { id: 'family', label: 'Familia', emoji: '👨‍👩‍👧', message: '¡Hola! ¿Quieres hablar de tu familia? Estoy aquí para escucharte. 💙' },
    { id: 'friends', label: 'Amigos', emoji: '🤗', message: '¡Hola! ¿Cómo están tus amigos? ¿Jugaste con alguien hoy? 🤗' },
    { id: 'sad', label: 'Me siento triste', emoji: '💙', message: '¡Hola! Estoy aquí contigo. ¿Qué te tiene triste? 💙' },
    { id: 'scared', label: 'Tengo miedo', emoji: '🌟', message: '¡Hola! Es normal tener miedo. ¿Quieres contarme qué te asusta? 🌟' },
    { id: 'help', label: 'Necesito ayuda', emoji: '🆘', message: '¡Hola! Estoy aquí para escucharte. ¿Qué pasó? 💙' },
    { id: 'talk', label: 'Hablar con Calmi', emoji: '💭', message: '¡Hola! ¿Sobre qué quieres hablar hoy? 💭' }
  ];

  const handleNewChatClick = () => {
    setShowThemeModal(true);
  };

  const handleTopicSelect = (topic: typeof topics[0]) => {
    const initialMessage: Message = {
      id: '1',
      text: topic.message,
      sender: 'calmi',
      timestamp: new Date()
    };
    const newChat = chatService.createChat({ title: topic.label, initialMessage });
    setChats([...chats, newChat]);
    setShowThemeModal(false);
    onNavigate('chat');
  };

  const handleOpenChat = (id: string) => {
    chatService.setCurrentChatId(id);
    onNavigate('chat');
  };

  const handleDeleteChat = (id: string) => {
    if (!tutorMode) return;
    chatService.deleteChat(id);
    setChats(chatService.getChats());
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return d.toLocaleDateString('es-ES') + ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <ThemedScreen className="p-4 sm:p-6 md:p-8 pb-24">
      <div className="w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => onNavigate('home')} 
            className="p-3 sm:p-4 bg-white/80 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all btn-interactive border border-slate-200"
          >
            <span className="text-xl sm:text-2xl">←</span>
          </button>
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">Mis chats 💬</h1>
          <div className="w-10"></div>
        </div>

        <div className="mb-6 text-center">
          <Button onClick={handleNewChatClick} className="bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow-md hover:shadow-lg transition-all">
            + Nuevo chat
          </Button>
        </div>

        {chats.length === 0 ? (
          <Card className="p-6 text-center bg-white/90 border border-slate-200 shadow-md">
            <p className="text-slate-600">Aún no tienes chats guardados.</p>
            <p className="text-slate-500 text-sm mt-2">Empieza uno nuevo y Calmi recordará la conversación.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {chats.map((chat) => (
              <Card
                key={chat.id}
                onClick={() => handleOpenChat(chat.id)}
                className="p-4 bg-white/90 border border-slate-200 shadow-md hover:shadow-lg transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-800 truncate">{chat.title}</h3>
                    <p className="text-xs text-slate-500">{formatDate(chat.updatedAt)}</p>
                    <p className="text-sm text-slate-600 truncate mt-1">
                      {chat.messages.length > 0 ? chat.messages[chat.messages.length - 1].text : 'Sin mensajes aún'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <span className="text-sm text-slate-500">{chat.messages.length} mensajes</span>
                    {tutorMode && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteChat(chat.id); }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-all"
                        title="Eliminar chat (solo tutor)"
                      >
                        🗑
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {showThemeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md p-6 bg-white/95 border border-blue-200 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">¿De qué quieres hablar?</h3>
                <button onClick={() => setShowThemeModal(false)} className="p-2 hover:bg-gray-100 rounded-full">✕</button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {topics.map((topic) => (
                  <button
                    key={topic.id}
                    onClick={() => handleTopicSelect(topic)}
                    className="p-4 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 hover:border-blue-400 hover:shadow-md transition-all text-left"
                  >
                    <div className="text-2xl mb-2">{topic.emoji}</div>
                    <div className="text-sm font-semibold text-slate-800">{topic.label}</div>
                  </button>
                ))}
              </div>
            </Card>
          </div>
        )}

        <NavigationBar activeItem="chat" onNavigate={onNavigate} />
      </div>
    </ThemedScreen>
  );
};

export default ChatListScreen;
